import { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  NumberInput,
  SegmentedControl,
  SimpleGrid,
  Skeleton,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { adminErrText, adminGet, adminSend, AdminApiError } from "../api.ts";
import {
  AdminForm,
  AdminFormActions,
  AdminFormModal,
  AdminFormSection,
} from "../components/AdminForm.tsx";
import { AdminTable } from "../components/AdminTable.tsx";
import { TablePagination } from "../components/TablePagination.tsx";
import { useClientPagination } from "../hooks/useClientPagination.ts";
import { OneClickCreateWizard } from "./OneClickCreateWizard.tsx";

interface BotItem {
  id: string;
  code: string;
  name: string;
  botUsername: string | null;
  isEnabled: boolean;
  currency: string;
  defaultLanguage: string;
  onboardingSource: "manual" | "managed";
  createdAt: string;
}

interface CreateForm {
  code: string;
  name: string;
  telegramBotToken: string;
  currency: string;
  miniAppUrl: string;
  defaultLanguage: string;
  botType: "lottery" | "analytics";
}

interface ImportForm {
  newBotTelegramId: number | string;
  suggestedUsername: string;
  suggestedName: string;
}

export function BotListPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["bots"],
    queryFn: () => adminGet<{ items: BotItem[] }>("/bots"),
    refetchInterval: 30_000,
  });
  const [manualOpen, setManualOpen] = useState(false);
  const [oneClickOpen, setOneClickOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [search, setSearch] = useState("");

  const form = useForm<CreateForm>({
    initialValues: {
      code: "",
      name: "",
      telegramBotToken: "",
      currency: "INR",
      miniAppUrl: "",
      defaultLanguage: "en-IN",
      botType: "lottery",
    },
    validate: {
      code: (v) => (v.trim().length === 0 ? "请填写 code" : null),
      name: (v) => (v.trim().length === 0 ? "请填写名称" : null),
      telegramBotToken: (v) => (v.trim().length === 0 ? "请填写 Bot Token" : null),
      miniAppUrl: (v, values) =>
        values.botType === "lottery" && v.trim().length === 0 ? "请填写 Mini App URL" : null,
    },
  });

  const importForm = useForm<ImportForm>({
    initialValues: {
      newBotTelegramId: "",
      suggestedUsername: "",
      suggestedName: "",
    },
    validate: {
      newBotTelegramId: (v) => (String(v).trim().length === 0 ? "请填写 Telegram Bot ID" : null),
    },
  });

  const create = useMutation({
    mutationFn: (values: CreateForm) => adminSend("POST", "/bots", values),
    onSuccess: () => {
      notifications.show({ color: "green", message: "创建成功" });
      setManualOpen(false);
      form.reset();
      void qc.invalidateQueries({ queryKey: ["bots"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });

  const importBot = useMutation({
    mutationFn: (values: ImportForm) =>
      adminSend<{ id: string; botUsername: string; miniAppUrl: string }>(
        "POST",
        "/managed-bots/import",
        {
          newBotTelegramId: Number(values.newBotTelegramId),
          suggestedUsername: values.suggestedUsername.trim() || undefined,
          suggestedName: values.suggestedName.trim() || undefined,
        },
      ),
    onSuccess: (r) => {
      notifications.show({
        color: "green",
        message: `导入成功：@${r.botUsername}`,
      });
      setImportOpen(false);
      importForm.reset();
      void qc.invalidateQueries({ queryKey: ["bots"] });
      window.location.href = `/bots/${r.id}/basic`;
    },
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });

  const deleteBot = useMutation({
    mutationFn: (id: string) => adminSend("DELETE", `/bots/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["bots"] });
      notifications.show({ color: "green", message: "已删除" });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });

  const toggleEnabled = useMutation({
    mutationFn: (args: { id: string; isEnabled: boolean }) =>
      adminSend("PATCH", `/bots/${args.id}`, { isEnabled: args.isEnabled }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["bots"] });
      notifications.show({ color: "green", message: "已更新启用状态" });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });

  const items = data?.items ?? [];

  const keyword = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      keyword
        ? items.filter(
            (r) =>
              r.code.toLowerCase().includes(keyword) ||
              r.name.toLowerCase().includes(keyword) ||
              (r.botUsername ?? "").toLowerCase().includes(keyword),
          )
        : items,
    [items, keyword],
  );
  const { pageItems, page, setPage, pageSize, total, totalPages } = useClientPagination(
    filtered,
    keyword,
  );

  const rows = pageItems.map((row) => (
    <Table.Tr
      key={row.id}
      style={{ cursor: "pointer" }}
      onClick={() => void navigate(`/bots/${row.id}/basic`)}
    >
      <Table.Td>{row.code}</Table.Td>
      <Table.Td>{row.name}</Table.Td>
      <Table.Td>{row.botUsername ?? "-"}</Table.Td>
      <Table.Td>
        {row.isEnabled ? <Badge color="green">启用</Badge> : <Badge color="gray">停用</Badge>}
      </Table.Td>
      <Table.Td>
        {row.onboardingSource === "managed" ? (
          <Badge color="violet">Managed</Badge>
        ) : (
          <Badge color="gray">Manual</Badge>
        )}
      </Table.Td>
      <Table.Td>{row.currency}</Table.Td>
      <Table.Td>{row.defaultLanguage}</Table.Td>
      <Table.Td
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Group gap="xs" wrap="nowrap">
          <Switch
            size="sm"
            checked={row.isEnabled}
            onLabel="启用"
            offLabel="停用"
            onChange={(e) =>
              toggleEnabled.mutate({ id: row.id, isEnabled: e.currentTarget.checked })
            }
          />
          <Button
            size="xs"
            variant="outline"
            color="red"
            onClick={() => {
              modals.openConfirmModal({
                title: "确认删除",
                children: (
                  <Text size="sm">
                    确定要删除 Bot「{row.name}」（code: {row.code}）吗？此操作不可撤销。
                  </Text>
                ),
                labels: { confirm: "删除", cancel: "取消" },
                confirmProps: { color: "red" },
                onConfirm: () => deleteBot.mutate(row.id),
              });
            }}
          >
            删除
          </Button>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <>
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="md">
          <Text fw={600} size="lg">
            机器人列表
          </Text>
          <Group gap="xs">
            <Button onClick={() => setOneClickOpen(true)}>一键创建（推荐）</Button>
            <Button variant="outline" onClick={() => setManualOpen(true)}>
              手粘 token
            </Button>
            <Button variant="light" onClick={() => setImportOpen(true)}>
              导入已存在
            </Button>
          </Group>
        </Group>

        {isError ? (
          <Alert color="red" title="加载失败" mb="md">
            数据加载失败，请刷新页面重试。
          </Alert>
        ) : null}

        <TextInput
          placeholder="搜索 code / 名称 / 用户名"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          mb="sm"
        />

        {isLoading ? (
          <Stack gap="xs">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} h={40} radius="sm" />
            ))}
          </Stack>
        ) : items.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="md">
              <Text size="lg" fw={600} c="dimmed">
                还没有机器人
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                创建第一个 Bot 后，所有抽奖数据都按 Bot 隔离。
                <br />
                推荐使用「一键创建」，自动完成 Token 配置与预检。
              </Text>
              <Group gap="xs">
                <Button onClick={() => setOneClickOpen(true)}>一键创建（推荐）</Button>
                <Button variant="outline" onClick={() => setManualOpen(true)}>
                  手粘 token
                </Button>
                <Button variant="light" onClick={() => setImportOpen(true)}>
                  导入已存在
                </Button>
              </Group>
            </Stack>
          </Center>
        ) : (
          <>
            <AdminTable minWidth={900}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>code</Table.Th>
                  <Table.Th>名称</Table.Th>
                  <Table.Th>Telegram 用户名</Table.Th>
                  <Table.Th>状态</Table.Th>
                  <Table.Th>来源</Table.Th>
                  <Table.Th>货币</Table.Th>
                  <Table.Th>默认语言</Table.Th>
                  <Table.Th>操作</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={8} style={{ textAlign: "center" }}>
                      <Text c="dimmed" py="md">
                        {keyword.length > 0 ? "未找到匹配 Bot" : "暂无 Bot"}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  rows
                )}
              </Table.Tbody>
            </AdminTable>
            <TablePagination
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      <AdminFormModal
        title="手粘 token 新建机器人"
        opened={manualOpen}
        onClose={() => setManualOpen(false)}
        size="xl"
      >
        <AdminForm onSubmit={form.onSubmit((v) => create.mutate(v))}>
          <AdminFormSection title="Bot 类型">
            <SegmentedControl
              data={[
                { label: "转盘抽奖", value: "lottery" },
                { label: "分析机器人", value: "analytics" },
              ]}
              {...form.getInputProps("botType")}
              fullWidth
            />
          </AdminFormSection>
          <AdminFormSection title="标识">
            <SimpleGrid cols={2}>
              <TextInput
                label="code"
                placeholder="lucky-india"
                required
                {...form.getInputProps("code")}
              />
              <TextInput label="名称" required {...form.getInputProps("name")} />
              {form.values.botType === "lottery" ? (
                <>
                  <TextInput label="货币" required {...form.getInputProps("currency")} />
                  <TextInput label="默认语言" {...form.getInputProps("defaultLanguage")} />
                </>
              ) : null}
            </SimpleGrid>
          </AdminFormSection>
          <AdminFormSection title="技术参数">
            <TextInput
              label="Bot Token"
              required
              type="password"
              {...form.getInputProps("telegramBotToken")}
            />
            {form.values.botType === "lottery" ? (
              <TextInput
                label="Mini App URL"
                required
                placeholder="https://app.example.com/lucky-india/"
                {...form.getInputProps("miniAppUrl")}
              />
            ) : null}
          </AdminFormSection>
          <AdminFormActions>
            <Button variant="default" type="button" onClick={() => setManualOpen(false)}>
              取消
            </Button>
            <Button type="submit" loading={create.isPending}>
              创建
            </Button>
          </AdminFormActions>
        </AdminForm>
      </AdminFormModal>

      <AdminFormModal
        title="导入已存在的 Bot（manager 已管理）"
        opened={importOpen}
        onClose={() => setImportOpen(false)}
      >
        <AdminForm onSubmit={importForm.onSubmit((v) => importBot.mutate(v))}>
          <NumberInput
            label="Telegram Bot ID"
            required
            placeholder="123456789"
            {...importForm.getInputProps("newBotTelegramId")}
          />
          <TextInput
            label="建议 username（可选）"
            placeholder="my_lucky_bot"
            {...importForm.getInputProps("suggestedUsername")}
          />
          <TextInput
            label="建议名称（可选）"
            placeholder="Lucky India Bot"
            {...importForm.getInputProps("suggestedName")}
          />
          <AdminFormActions>
            <Button variant="default" type="button" onClick={() => setImportOpen(false)}>
              取消
            </Button>
            <Button type="submit" loading={importBot.isPending}>
              导入
            </Button>
          </AdminFormActions>
        </AdminForm>
      </AdminFormModal>

      <OneClickCreateWizard
        open={oneClickOpen}
        onClose={() => setOneClickOpen(false)}
        onCompleted={(botId) => {
          setOneClickOpen(false);
          window.location.href = `/bots/${botId}/basic`;
        }}
      />
    </>
  );
}
